import React from 'react';
import useThemeColors from '@/hooks/useThemeColors';
import useInertiaData from '@/hooks/useInertiaData';
import { Heading } from '@/components/Typography';
import { BookOpen } from 'lucide-react';
import { Link } from '@inertiajs/react';
import { useTranslation } from 'react-i18next';

export interface HomeCourseData {
    id: number;
    title: string;
    slug: string;
    category: {
        id: number;
        name: string;
    };
    progress_percentage: number;
    last_activity?: string;
}

interface HomeCourseCardProps {
    course: HomeCourseData;
}

const HomeCourseCard: React.FC<HomeCourseCardProps> = ({ course }) => {
    const colors = useThemeColors();
    const { t } = useTranslation();
    const { account_paused } = useInertiaData();

    const cardColors = [
        'bg-emerald-500',
        'bg-blue-500',
        'bg-purple-500',
        'bg-orange-500',
        'bg-pink-500',
        'bg-indigo-500'
    ];
    const colorClass = cardColors[course.id % cardColors.length];

    const cardContent = (
        <div className="space-y-4">
            <div className="flex items-start justify-between">
                <div className="min-w-0 flex-1 space-y-2">
                    <p className="course-category">
                        {course.category.name}
                    </p>
                    <Heading as="h3" className={`course-title ${account_paused ? '' : colors.hoverText}`}>
                        {course.title}
                    </Heading>
                </div>
                <div className={`h-10 w-10 sm:h-12 sm:w-12 ${colorClass} ml-4 flex flex-shrink-0 items-center justify-center rounded-lg`}>
                    <BookOpen size={20} className="text-white sm:h-6 sm:w-6" />
                </div>
            </div>

            <div className="space-y-2">
                <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600 dark:text-neutral-400">
                        {course.progress_percentage}{t('home.percentCompleted')}
                    </span>
                    <span className="text-sm text-gray-500 dark:text-neutral-400">
                        {course.progress_percentage}%
                    </span>
                </div>
                <div className="h-2 w-full rounded-full bg-gray-200 dark:bg-neutral-700">
                    <div
                        className={`h-2 ${colorClass} rounded-full transition-all duration-500`}
                        style={{ width: `${course.progress_percentage}%` }}
                    ></div>
                </div>
            </div>

            {course.last_activity && (
                <p className="truncate text-sm text-gray-500 dark:text-neutral-400">
                    {t('home.lastActivity')} {course.last_activity}
                </p>
            )}
        </div>
    );

    if (account_paused) {
        return (
            <div className="course-card block cursor-not-allowed grayscale opacity-60">
                {cardContent}
            </div>
        );
    }

    return (
        <Link href={`/courses/${course.slug}`} className="group course-card block">
            {cardContent}
        </Link>
    );
};

export default HomeCourseCard;